import { Modal } from '@/components/modal';
import CountryCodeSelect from '@/components/CountryCodeSelect/CountryCodeSelect';
import { ErrorApiResponse } from '@/types/api.types';
import {
  CreateDMOStaffPayload,
  DMOStaff,
} from '@/types/dmo-staff-management.types';
import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialData?: DMOStaff | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateDMOStaffPayload) => Promise<void>;
}

const DMOStaffFormModal = ({
  open,
  mode,
  initialData,
  isSubmitting,
  onClose,
  onSubmit,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const [formData, setFormData] = useState<CreateDMOStaffPayload>({
    name: '',
    email: '',
    country_short_code: '',
    country_code: '',
    phone_country_short_code: '',
    phone_number: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && initialData) {
      setFormData({
        name: String(initialData.name || ''),
        email: String(initialData.email || ''),
        country_short_code: String(initialData.country_short_code || ''),
        country_code: String(initialData.country_code || ''),
        phone_country_short_code: String(initialData.phone_country_short_code || initialData.country_short_code || ''),
        phone_number: String(initialData.phone_number || ''),
      });
    } else {
      setFormData({
        name: '',
        email: '',
        country_short_code: '',
        country_code: '',
        phone_country_short_code: '',
        phone_number: '',
      });
    }

    setErrors({});
  }, [open, mode, initialData]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};

    if (!formData.name.trim()) next.name = 'Name is required';
    if (!formData.email.trim()) next.email = 'Email is required';

    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email.trim() && !pattern.test(formData.email.trim())) {
      next.email = 'Please enter a valid email';
    }

    if (!formData.country_short_code?.trim()) {
      next.country_short_code = 'Country is required';
    }

    if (!formData.phone_number?.trim()) {
      next.phone_number = 'Phone number is required';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      await onSubmit({
        name: formData.name.trim(),
        email: formData.email.trim(),
        country_short_code: formData.country_short_code?.trim() || null,
        country_code: formData.country_code?.trim() || null,
        phone_country_short_code: formData.phone_country_short_code?.trim() || formData.country_short_code?.trim() || null,
        phone_number: formData.phone_number?.trim() || null,
      });
      onClose();
    } catch (err) {
      const message =
        (err as ErrorApiResponse)?.data?.message ||
        (err as ErrorApiResponse)?.message ||
        'Failed to submit DMO staff';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const title = mode === 'edit' ? 'Edit DMO Staff' : 'Add DMO Staff';

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="!flex items-center justify-center"
      aria-labelledby="dmo-staff-form-modal-title"
    >
      <div className="modal-content w-full max-w-lg mx-4 contrast:!bg-black contrast:!text-white contrast:!border contrast:!border-white/60 contrast:!ring-1 contrast:!ring-white/40">
        <div className="modal-header contrast:!border-b contrast:!border-white/20">
          <h1 id="dmo-staff-form-modal-title" className="modal-title">
            {title}
          </h1>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-700 dark:hover:text-gray-900 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="modal-body space-y-4">
          <div className="space-y-1">
            <label
              htmlFor="dmo-staff-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-800"
            >
              Name
            </label>
            <input
              id="dmo-staff-name"
              name="name"
              type="text"
              value={formData.name}
              required
              autoComplete="name"
              aria-required="true"
              aria-invalid={Boolean(errors.name)}
              aria-describedby={
                errors.name ? 'dmo-staff-name-error' : undefined
              }
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, name: e.target.value }));
                if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
              }}
              className="input input-md w-full contrast:!bg-black contrast:!text-white contrast:!border-gray-600"
              placeholder="Enter staff name"
              disabled={isSubmitting}
            />
            {errors.name && (
              <p
                id="dmo-staff-name-error"
                className="text-xs text-red-600 contrast:!text-white"
                role="alert"
              >
                {errors.name}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label
              htmlFor="dmo-staff-email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-800"
            >
              Email
            </label>
            <input
              id="dmo-staff-email"
              name="email"
              type="email"
              value={formData.email}
              required
              autoComplete="email"
              aria-required="true"
              aria-invalid={Boolean(errors.email)}
              aria-describedby={
                errors.email ? 'dmo-staff-email-error' : undefined
              }
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, email: e.target.value }));
                if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
              }}
              className="input input-md w-full contrast:!bg-black contrast:!text-white contrast:!border-gray-600"
              placeholder="staff@example.com"
              disabled={isSubmitting || mode === 'edit'}
            />
            {errors.email && (
              <p
                id="dmo-staff-email-error"
                className="text-xs text-red-600 contrast:!text-white"
                role="alert"
              >
                {errors.email}
              </p>
            )}
            {mode === 'edit' && (
              <p className="text-xs text-gray-500 dark:text-gray-600 contrast:!text-white/80">
                Email cannot be changed.
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label
              id="dmo-staff-country-label"
              htmlFor="dmo-staff-country"
              className="block text-sm font-medium text-gray-700 dark:text-gray-800"
            >
              Country
            </label>
            <CountryCodeSelect
              id="dmo-staff-country"
              ariaLabelledBy="dmo-staff-country-label"
              ariaDescribedBy={
                errors.country_short_code
                  ? 'dmo-staff-country-error'
                  : undefined
              }
              selected={formData.country_short_code || ''}
              setFieldValue={(dialCode, shortCode) => {
                setFormData((prev) => ({
                  ...prev,
                  country_short_code: shortCode,
                  ...(!prev.phone_country_short_code
                    ? {
                        phone_country_short_code: shortCode,
                        country_code: dialCode,
                      }
                    : {}),
                }));
                if (errors.country_short_code) {
                  setErrors((prev) => ({ ...prev, country_short_code: '' }));
                }
              }}
              hasError={Boolean(errors.country_short_code)}
              intl={intl}
              useFullCountryName
              disabled={isSubmitting}
              ariaLabel={
                errors.country_short_code
                  ? 'Country. Error: Country is required.'
                  : 'Country'
              }
            />
            {errors.country_short_code && (
              <p
                id="dmo-staff-country-error"
                className="text-xs text-red-600 contrast:!text-white"
                role="alert"
              >
                {errors.country_short_code}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label
              htmlFor="dmo-staff-phone"
              className="block text-sm font-medium text-gray-700 dark:text-gray-800"
            >
              Phone number
            </label>
            <div className="flex gap-2">
              <div className="w-32 flex-shrink-0">
                <CountryCodeSelect
                  id="dmo-staff-phone-country"
                  selected={
                    formData.phone_country_short_code ||
                    formData.country_short_code ||
                    ''
                  }
                  setFieldValue={(dialCode, shortCode) => {
                    setFormData((prev) => ({
                      ...prev,
                      country_code: dialCode,
                      phone_country_short_code: shortCode,
                    }));
                    if (errors.phone_number) {
                      setErrors((prev) => ({ ...prev, phone_number: '' }));
                    }
                  }}
                  intl={intl}
                  disabled={isSubmitting}
                  hasError={Boolean(errors.phone_number)}
                  ariaLabel="Phone country code"
                />
              </div>
              <input
                id="dmo-staff-phone"
                name="tel"
                type="tel"
                value={formData.phone_number || ''}
                required
                autoComplete="tel"
                aria-required="true"
                aria-invalid={Boolean(errors.phone_number)}
                aria-describedby={
                  errors.phone_number ? 'dmo-staff-phone-error' : undefined
                }
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    phone_number: e.target.value,
                  }));
                  if (errors.phone_number) {
                    setErrors((prev) => ({ ...prev, phone_number: '' }));
                  }
                }}
                className="input input-md w-full contrast:!bg-black contrast:!text-white contrast:!border-gray-600"
                placeholder="Enter phone number"
                disabled={isSubmitting}
              />
            </div>
            {errors.phone_number && (
              <p
                id="dmo-staff-phone-error"
                className="text-xs text-red-600 contrast:!text-white"
                role="alert"
              >
                {errors.phone_number}
              </p>
            )}
          </div>

          {mode === 'create' && (
            <div className="rounded-md bg-gray-50 dark:bg-gray-100 p-3 text-xs text-gray-600 dark:text-gray-700 contrast:!bg-black contrast:!text-white">
              On submit, an email will be sent to the staff with login
              credentials.
            </div>
          )}
        </div>

        <div className="modal-footer flex justify-end gap-2 contrast:!border-t contrast:!border-white/20">
          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DMOStaffFormModal;
